import {
  GoogleMap,
  InfoWindow,
  Marker,
  MarkerClusterer,
  useJsApiLoader,
} from '@react-google-maps/api';
import { Empty, InputNumber, Select, Spin, Tooltip } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery } from '@tanstack/react-query';

import AddressSearchInput, {
  PlaceSuggestion,
} from '../../components/AddressSearchInput';
import { Geolocation } from '../../types/Location';
import { InstructorMarkerData, InstructorSearchResult } from '../types/User';
import UserAPI from '../UserAPI';

import Table from '../../components/Table';
import CourseAPI from '../../course/CourseAPI';
import Course from '../../course/types/Course';
import { ColumnsType } from '../../types/ColumnTypes';
import InstructorMarkerInfoWindow from './InstructorMarkerInfoWindow';
import UserCell from './UserCell';
import { InfoCircleOutlined } from '@ant-design/icons';
import InfiniteScroll from 'react-infinite-scroller';

const DEFAULT_DISTANCE = 10;

type InstructorMapProps = {
  initialCenter?: Geolocation;
  initialZoom?: number;
  className?: string;
};

const InstructorMap: React.FC<InstructorMapProps> = ({
  initialCenter,
  initialZoom,
  className,
}) => {
  const { t } = useTranslation();

  const apiKey = process.env.REACT_APP_MAPS_API_KEY ?? '';

  const defaultZoom = 11;
  const [defaultLat, defaultLng] = [59.3293, 18.0686];

  const [zoom, setZoom] = useState(initialZoom ?? defaultZoom);
  const [center, setCenter] = useState<google.maps.LatLngLiteral>({
    lat: initialCenter?.lat ?? defaultLat,
    lng: initialCenter?.lng ?? defaultLng,
  });
  const [address, setAddress] = useState<PlaceSuggestion | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const lastMarkerRequestRef = useRef(0);
  const [markerData, setMarkerData] = useState<InstructorMarkerData[]>();
  const [loadingMarkerData, setLoadingMarkerData] = useState(false);
  const [courses, setCourses] = useState<Course[]>();
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [selectedCourses, setSelectedCourses] = useState<number[]>([]);
  const [displayInfoWindow, setDisplayInfoWindow] = useState(false);
  const [infoWindowContent, setInfoWindowContent] =
    useState<InstructorMarkerData>();

  const [map, setMap] = useState<google.maps.Map | null>();
  const [infoWindowPosition, setInfoWindowPosition] =
    useState<google.maps.LatLngLiteral>();

  const onChangeSearchBox = useCallback(
    (suggestion?: PlaceSuggestion | null) => {
      if (suggestion) {
        setCenter({
          lat: suggestion.geolocation.lat,
          lng: suggestion.geolocation.lng,
        });
        setZoom(defaultZoom);
      }
      setAddress(suggestion ?? null);
    },
    [],
  );

  const fetchInstructorMarkerData = async (
    _bounds: google.maps.LatLngBounds,
    courseIds?: number[],
  ) => {
    setLoadingMarkerData(true);
    const currentRequest = ++lastMarkerRequestRef.current;
    try {
      const params = {
        southWestLat: _bounds.getSouthWest().lat(),
        southWestLng: _bounds.getSouthWest().lng(),
        northEastLat: _bounds.getNorthEast().lat(),
        northEastLng: _bounds.getNorthEast().lng(),
        courseIds: courseIds,
      };
      const { data } = await UserAPI.getInstructorMarkerData(params);
      if (currentRequest === lastMarkerRequestRef.current) {
        setMarkerData(data);
      }
    } finally {
      if (currentRequest === lastMarkerRequestRef.current) {
        setLoadingMarkerData(false);
      }
    }
  };

  const instructorListQuery = useInfiniteQuery({
    queryKey: ['instructorList', { selectedCourses, address, distance }],
    queryFn: ({ pageParam }) =>
      UserAPI.getInstructorList({
        courseIds: selectedCourses,
        lat: address?.geolocation.lat,
        lng: address?.geolocation.lng,
        radius: address ? (distance ?? DEFAULT_DISTANCE) : undefined,
        page: pageParam,
      }).then(({ data }) => data),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.paginationMeta.page < lastPage.paginationMeta.lastPage
        ? lastPage.paginationMeta.page + 1
        : undefined,
  });

  const instructorList = useMemo(
    () => instructorListQuery?.data?.pages.flatMap((page) => page.data) ?? [],
    [instructorListQuery],
  );

  useEffect(() => {
    setLoadingCourses(true);
    CourseAPI.getCourses()
      .then(({ data }) => setCourses(data))
      .finally(() => setLoadingCourses(false));
  }, []);

  useEffect(() => {
    const bounds = map?.getBounds();
    if (bounds) {
      fetchInstructorMarkerData(bounds, selectedCourses);
    }
  }, [selectedCourses, map]);

  const onLoad = (map: google.maps.Map) => {
    setMap(map);
  };

  const onUnmount = useCallback(function callback(map: google.maps.Map) {
    setMap(null);
  }, []);

  const onIdle = () => {
    const bounds = map?.getBounds();
    if (bounds) {
      fetchInstructorMarkerData(bounds, selectedCourses);
    }
  };

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
  });

  const columns = useMemo<ColumnsType<InstructorSearchResult>>(
    () => [
      {
        title: t('common.name'),
        render: (isr) => <UserCell user={isr.user} hideGravatar />,
      },
      {
        title: t('common.phoneNumber'),
        render: (isr) => <div>{isr.user.phoneNumber ?? '-'}</div>,
      },
      {
        title: t('views.Users.distance'),
        render: (isr) => (
          <div>
            {typeof isr.distance === 'number'
              ? t('views.Users.instructorDistance', { distance: isr.distance })
              : '-'}
          </div>
        ),
      },
    ],
    [t],
  );

  return (
    <>
      <div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 items-start gap-x-2 mb-4">
          <div className="col-span-2 md:col-span-1">
            <p className="font-medium text-sm mb-0.5">{t('common.address')}</p>
            <AddressSearchInput onChange={onChangeSearchBox} />
          </div>

          <div>
            <p className="font-medium text-sm mb-0.5">{t('common.courses')}</p>
            <Select
              className="w-full"
              mode="multiple"
              allowClear
              value={selectedCourses}
              onChange={(value) => setSelectedCourses(value)}
              loading={loadingCourses}
              filterOption={(input, option) =>
                option?.props.children
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }>
              {courses?.map((course) => (
                <Select.Option key={course.id} value={course.id}>
                  {course.name}
                </Select.Option>
              ))}
            </Select>
          </div>

          <div>
            <p className="font-medium text-sm mb-0.5">
              {t('views.Users.distance') + ' '}
              <Tooltip title={t('views.Users.distanceHint')}>
                <InfoCircleOutlined />
              </Tooltip>
            </p>
            <InputNumber
              className="w-full"
              value={distance}
              onChange={(value) => setDistance(value)}
              placeholder={String(DEFAULT_DISTANCE)}
              min={1}
              suffix="km"
            />
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div
          className={className}
          style={{ height: '700px', position: 'relative' }}>
          {isLoaded ? (
            <>
              <GoogleMap
                mapContainerClassName="w-full h-full rounded-lg"
                clickableIcons={false}
                options={{
                  styles: [
                    {
                      elementType: 'labels.icon',
                      stylers: [{ visibility: 'off' }],
                    },
                  ],
                  mapId: '26a46e9c4fbf9e24',
                  mapTypeControl: false,
                  streetViewControl: false,
                }}
                onUnmount={onUnmount}
                onLoad={onLoad}
                onIdle={onIdle}
                center={center}
                zoom={zoom}>
                {infoWindowPosition && displayInfoWindow && (
                  <InfoWindow
                    position={infoWindowPosition}
                    options={{
                      pixelOffset: new window.google.maps.Size(0, -32),
                    }}
                    onCloseClick={() => setDisplayInfoWindow(false)}>
                    <>
                      {infoWindowContent && (
                        <InstructorMarkerInfoWindow
                          markerData={infoWindowContent}
                        />
                      )}
                    </>
                  </InfoWindow>
                )}
                {address && (
                  <Marker position={address.geolocation} cursor="grab" />
                )}
                {markerData && (
                  <MarkerClusterer
                    maxZoom={11}
                    averageCenter
                    minimumClusterSize={4}>
                    {(clusterer) => (
                      <>
                        {markerData.map((data) => {
                          const onClickMarker = () => {
                            setInfoWindowPosition(data.location);
                            setDisplayInfoWindow(true);
                            setInfoWindowContent(data);
                          };

                          const onUnmount = () => {
                            if (infoWindowContent?.userId === data.userId) {
                              setDisplayInfoWindow(false);
                            }
                          };

                          return (
                            <Marker
                              key={data.userId}
                              onClick={onClickMarker}
                              title={data.name}
                              clusterer={clusterer}
                              onUnmount={onUnmount}
                              icon={{
                                url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512' role='img' aria-labelledby='title desc'%3E%3Cpath d='M256 501C237 459 72 287 72 183C72 87 154 9 256 9s184 78 184 174c0 104-165 276-184 318Z' fill='%23d77329' stroke='%23c05621' stroke-width='15' /%3E%3Ccircle cx='256' cy='127' r='43' fill='%23fff' stroke='%23c05621' stroke-width='5'/%3E%3Cpath d='M169 265c0-50 38-79 87-79s87 29 87 79v12c0 14-11 25-25 25H194c-14 0-25-11-25-25v-12Z' fill='%23fff' stroke='%23c05621' stroke-width='5' /%3E%3C/svg%3E%0A",
                                scaledSize: new google.maps.Size(36, 36),
                              }}
                              position={
                                new google.maps.LatLng(
                                  data.location.lat,
                                  data.location.lng,
                                )
                              }
                            />
                          );
                        })}
                      </>
                    )}
                  </MarkerClusterer>
                )}
              </GoogleMap>
              <Spin
                spinning={loadingMarkerData}
                style={{ position: 'absolute', top: '0.5rem', left: '0.5rem' }}
              />
            </>
          ) : (
            <Spin />
          )}
        </div>

        <InfiniteScroll
          loadMore={() => {
            if (
              instructorListQuery.hasNextPage &&
              !instructorListQuery.isFetchingNextPage
            ) {
              instructorListQuery.fetchNextPage();
            }
          }}
          hasMore={
            !!instructorListQuery.hasNextPage &&
            !instructorListQuery.isFetchingNextPage
          }>
          <Table
            rowKey={(row) => row.user.id ?? ''}
            rows={instructorList}
            columns={columns}
            loading={
              instructorListQuery.isFetching &&
              !instructorListQuery.isFetchingNextPage
            }
            customLastRow={
              !instructorList.length &&
              !instructorListQuery.isFetching && (
                <tr className="text-center">
                  <td colSpan={columns.length}>
                    <Empty
                      className="mt-4 mb-6"
                      description={t('views.Users.noInstructors')}
                    />
                  </td>
                </tr>
              )
            }
          />
          {instructorListQuery.isFetching && (
            <div className="flex justify-center py-4">
              <Spin />
            </div>
          )}
        </InfiniteScroll>
      </div>
    </>
  );
};

export default InstructorMap;
